// Country signal helpers for PIN/ZIP handling.
//
// Some countries (e.g. UAE, Qatar) have no postal code system, so a PIN/ZIP code
// must not be required for their users. We derive the country from the signals we
// already have on the logged-in user — primarily the mobile number, which is stored
// with its dialing-code prefix and no '+', e.g. "9190..." (India) or "97150..." (UAE).
//
// This list mirrors NO_POSTAL_CODE_DIAL_CODES in the chess and chatbot backends —
// keep them in sync.
const NO_POSTAL_CODE_DIAL_CODES = ['971', '974', '965', '973', '968', '852', '853', '353'];

const NO_POSTAL_CODE_COUNTRIES = [
  'united arab emirates',
  'uae',
  'qatar',
  'kuwait',
  'bahrain',
  'oman',
  'hong kong',
  'macau',
  'macao',
  'ireland',
];

type CountrySignal = {
  mobile_number?: string | null;
  country_code?: string | null;
  country?: string | null;
};

const onlyDigits = (value?: string | null) => (value || '').replace(/\D/g, '');

/**
 * True only when we can positively determine the user's country has no postal code
 * system. Unknown or other countries return false, preserving existing behaviour
 * (i.e. we only ever relax the requirement, never tighten it).
 */
export function isNoPostalCodeCountry(user?: CountrySignal | null): boolean {
  if (!user) return false;

  const country = (user.country || '').trim().toLowerCase();
  if (country && NO_POSTAL_CODE_COUNTRIES.includes(country)) return true;

  const dial = onlyDigits(user.country_code);
  if (dial && NO_POSTAL_CODE_DIAL_CODES.includes(dial)) return true;

  const mobile = onlyDigits(user.mobile_number);
  if (mobile && NO_POSTAL_CODE_DIAL_CODES.some(code => mobile.startsWith(code))) return true;

  return false;
}

/** Whether a PIN/ZIP code should be required for this user. */
export function isPostalCodeRequired(user?: CountrySignal | null): boolean {
  return !isNoPostalCodeCountry(user);
}

/**
 * Country-aware PIN/ZIP format check — mirrors the backend `is_valid_pincode`
 * (chess/utils.py, chatbot/utils.py). Keep the rules in sync.
 *
 * - Empty is treated as valid here (whether it is REQUIRED is decided separately
 *   by isPostalCodeRequired).
 * - No-postal-code countries (UAE, ...): any value accepted.
 * - India (mobile prefix 91) or a bare 6-digit numeric: strict ^[1-9][0-9]{5}$.
 * - Everyone else (foreign / alphanumeric): letters, digits, spaces, hyphens, length 2-12.
 */
export function isValidPincode(pincode?: string | null, user?: CountrySignal | null): boolean {
  const code = (pincode || '').trim();
  if (!code) return true;
  if (isNoPostalCodeCountry(user)) return true;

  const mobile = onlyDigits(user?.mobile_number);
  if (mobile.startsWith('91')) return /^[1-9][0-9]{5}$/.test(code);
  if (!mobile && /^\d{6}$/.test(code)) return /^[1-9][0-9]{5}$/.test(code);
  return /^[A-Za-z0-9][A-Za-z0-9 -]{1,11}$/.test(code);
}
